import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Trophy, Medal, Award, TrendingUp, Package, RefreshCw, Tv, Users, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import apiClient from '@/services/apiClient';

/**
 * TV Display Mode for Pieces Processed Staff Performance
 * 
 * Features:
 * - Auto-refresh every 30 seconds
 * - Large, readable fonts for TV display
 * - Supports URL params: ?filter=today|month
 * - Optimized for 1920x1080 display
 */
const PiecesProcessedTV = () => {
  const [searchParams] = useSearchParams();
  
  // Get filter from URL or default to today
  const filterParam = searchParams.get('filter') || 'today';
  
  const [activeFilter, setActiveFilter] = useState(filterParam);
  const [staffPerformance, setStaffPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [stats, setStats] = useState({ staff_count: 0, total_pieces: 0, total_bundles: 0 });
  
  const fetchPerformance = useCallback(async () => {
    try {
      const response = await apiClient.get(`/sections/dispatch/pieces-processed/all-staff-performance?date_filter=${activeFilter}`);
      setStaffPerformance(response.data.staff_performance || []);
      setStats({
        staff_count: response.data.staff_count || 0,
        total_pieces: response.data.total_pieces || 0,
        total_bundles: response.data.total_bundles || 0
      });
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to load performance:', error);
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);
  
  // Initial fetch and auto-refresh every 30 seconds
  useEffect(() => {
    fetchPerformance();
    const interval = setInterval(fetchPerformance, 30000);
    return () => clearInterval(interval);
  }, [fetchPerformance]);
  
  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-12 h-12 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-10 h-10 text-gray-300" />;
    if (rank === 3) return <Award className="w-9 h-9 text-amber-600" />;
    return <span className="w-10 h-10 flex items-center justify-center text-gray-400 font-bold text-3xl">#{rank}</span>;
  };
  
  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-IN', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit',
        hour12: true 
      });
    } catch {
      return '-';
    }
  };

  // Get top 12 for TV display
  const topStaff = staffPerformance.slice(0, 12);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0f1a] via-[#111827] to-[#1a1f2e] text-white p-6">
      {/* Header */}
      <header className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="bg-gradient-to-br from-emerald-400 to-teal-500 p-3 rounded-2xl">
            <Package className="w-10 h-10 text-[#1a1f2e]" />
          </div>
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Pieces Processed</h1>
            <p className="text-xl text-gray-400">Staff Performance Leaderboard</p>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          {/* Filter Badge */}
          <Badge className={`text-lg py-2 px-4 ${activeFilter === 'today' ? 'bg-blue-500/20 text-blue-300' : 'bg-purple-500/20 text-purple-300'}`}>
            {activeFilter === 'today' ? '📅 Today' : '📆 This Month'}
          </Badge>
          
          {/* Live Badge */}
          <div className="flex items-center gap-2">
            <span className="relative flex h-4 w-4">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-4 w-4 bg-green-500"></span>
            </span>
            <span className="text-green-400 text-lg font-medium">LIVE</span>
          </div>
          
          {/* Last Refresh */}
          <div className="text-gray-400 text-lg">
            <Tv className="w-6 h-6 inline mr-2" />
            Updated: {lastRefresh.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </header>
      
      {/* Filter Selector */}
      <div className="flex justify-center gap-4 mb-8">
        {[
          { value: 'today', label: 'Today' },
          { value: 'month', label: 'This Month' }
        ].map((filter) => (
          <button
            key={filter.value}
            onClick={() => setActiveFilter(filter.value)}
            className={`flex items-center gap-3 px-8 py-4 rounded-xl text-xl font-semibold transition-all ${
              activeFilter === filter.value 
                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/30' 
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
            data-testid={`tv-filter-${filter.value}`}
          >
            {filter.label}
          </button>
        ))}
      </div>
      
      {/* Summary Stats Bar */}
      <div className="grid grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-2xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-400 text-lg mb-1">Total Staff</p>
              <p className="text-5xl font-bold">{stats.staff_count}</p>
            </div>
            <Users className="w-14 h-14 text-blue-400/50" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 rounded-2xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-400 text-lg mb-1">Total Pieces</p>
              <p className="text-5xl font-bold">{stats.total_pieces.toLocaleString()}</p>
            </div>
            <TrendingUp className="w-14 h-14 text-emerald-400/50" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-2xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-400 text-lg mb-1">Total Bundles</p>
              <p className="text-5xl font-bold">{stats.total_bundles.toLocaleString()}</p>
            </div>
            <Package className="w-14 h-14 text-purple-400/50" />
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center h-[50vh]">
          <RefreshCw className="w-16 h-16 animate-spin text-gray-500" />
        </div>
      ) : topStaff.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-[50vh] text-gray-500">
          <Users className="w-24 h-24 mb-4" />
          <p className="text-2xl">No data for this period</p>
          <p className="text-lg mt-2">Staff performance will appear here when pieces are processed</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-6">
          {/* Left Column - Top 6 */}
          <div className="space-y-4">
            {topStaff.slice(0, 6).map((staff, idx) => (
              <div
                key={staff.staff_id}
                className={`flex items-center gap-5 p-5 rounded-2xl transition-all ${
                  idx === 0 
                    ? 'bg-gradient-to-r from-yellow-500/20 to-amber-500/10 border-2 border-yellow-500/50' 
                    : idx === 1 
                    ? 'bg-gradient-to-r from-gray-400/20 to-gray-500/10 border border-gray-400/40'
                    : idx === 2
                    ? 'bg-gradient-to-r from-amber-600/20 to-amber-700/10 border border-amber-600/40'
                    : 'bg-white/5 border border-white/10'
                }`}
              >
                {/* Rank Icon */}
                <div className="flex-shrink-0">
                  {getRankIcon(idx + 1)}
                </div>
                
                {/* Name & ID */}
                <div className="flex-grow min-w-0">
                  <p className="text-2xl font-bold truncate">{staff.staff_name || staff.staff_id}</p>
                  <p className="text-gray-500">{staff.staff_id}</p>
                </div>
                
                {/* Stats */}
                <div className="flex gap-8 text-center">
                  <div>
                    <p className="text-3xl font-bold text-emerald-400">{staff.total_pieces.toLocaleString()}</p>
                    <p className="text-sm text-gray-500">Pieces</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-purple-400">{staff.total_bundles}</p>
                    <p className="text-sm text-gray-500">Bundles</p>
                  </div>
                  <div>
                    <p className="text-xl font-medium text-gray-400">{formatTime(staff.last_scan)}</p>
                    <p className="text-sm text-gray-500">Last Scan</p>
                  </div>
                </div>
                
                {/* Progress Bar based on pieces (relative to top performer) */}
                <div className="w-32 flex-shrink-0">
                  <Progress 
                    value={(staff.total_pieces / (topStaff[0]?.total_pieces || 1)) * 100} 
                    className="h-3"
                  />
                </div>
              </div>
            ))}
          </div>
          
          {/* Right Column - Positions 7-12 */}
          <div className="space-y-4">
            {topStaff.slice(6, 12).map((staff, idx) => (
              <div
                key={staff.staff_id}
                className="flex items-center gap-5 p-5 rounded-2xl bg-white/5 border border-white/10"
              >
                {/* Rank */}
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-xl bg-white/10">
                  <span className="text-2xl font-bold text-gray-400">#{idx + 7}</span>
                </div>
                
                {/* Name & ID */}
                <div className="flex-grow min-w-0">
                  <p className="text-xl font-bold truncate">{staff.staff_name || staff.staff_id}</p>
                  <p className="text-sm text-gray-500">{staff.staff_id}</p>
                </div>
                
                {/* Stats */}
                <div className="flex gap-6 text-center">
                  <div>
                    <p className="text-2xl font-bold text-emerald-400">{staff.total_pieces.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">Pieces</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-400">{staff.total_bundles}</p>
                    <p className="text-xs text-gray-500">Bundles</p>
                  </div>
                  <div>
                    <p className="text-lg font-medium text-gray-400">{formatTime(staff.last_scan)}</p>
                    <p className="text-xs text-gray-500">Last Scan</p>
                  </div>
                </div>
                
                {/* Progress */}
                <div className="w-24 flex-shrink-0">
                  <Progress 
                    value={(staff.total_pieces / (topStaff[0]?.total_pieces || 1)) * 100} 
                    className="h-2"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-[#0a0f1a]/80 backdrop-blur-sm border-t border-white/10 py-3 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <p className="text-gray-500">
            Auto-refreshes every 30 seconds
          </p>
          <p className="text-gray-500">
            Pieces Processed Performance Dashboard
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PiecesProcessedTV;
